/**
 * Dependency-free domain model and seed data for the Team Leave Tracker.
 *
 * All dates are computed relative to "today" at module load so the seeded
 * leaves always land in the current and next two months. This module is the
 * single seam to replace when a real leave API becomes available.
 */

import type { Entity, UserEntity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';

import { terracotta } from '../theme';

export type LeaveTypeId =
  | 'annual'
  | 'conference'
  | 'parental'
  | 'personal'
  | 'sick';

export interface LeaveType {
  id: LeaveTypeId;
  label: string;
  /** Hex colour used for pills, badges and the legend. */
  color: string;
}

export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  /** Avatar background colour. */
  color: string;
}

export interface LeaveEntry {
  id: string;
  memberId: string;
  /** Inclusive ISO date (YYYY-MM-DD), local. */
  startDate: string;
  /** Inclusive ISO date (YYYY-MM-DD), local. */
  endDate: string;
  type: LeaveTypeId;
  note?: string;
}

// ---------------------------------------------------------------------------
// Static reference data
// ---------------------------------------------------------------------------

export const LEAVE_TYPES: LeaveType[] = [
  { id: 'annual', label: 'Annual', color: '#2F6F5E' },
  { id: 'sick', label: 'Sick', color: terracotta },
  { id: 'personal', label: 'Personal', color: '#CC8B2A' },
  { id: 'conference', label: 'Conference', color: '#3A6EA5' },
  { id: 'parental', label: 'Parental', color: '#7B5EA7' },
];

export const LEAVE_TYPE_BY_ID: Record<LeaveTypeId, LeaveType> =
  LEAVE_TYPES.reduce(
    (acc, type) => ({ ...acc, [type.id]: type }),
    {} as Record<LeaveTypeId, LeaveType>,
  );

/** Warm, archival-friendly avatar colours assigned deterministically. */
const AVATAR_COLORS = [
  '#1A2332',
  '#C84B31',
  '#2F6F5E',
  '#CC8B2A',
  '#7B5EA7',
  '#3A6EA5',
  '#B5546F',
  '#4A5A78',
];

/** Stable hash → avatar colour for a given key (entity ref). */
export function colorForKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** Derives up to two uppercase initials from a display name. */
export function initialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Humanizes a catalog entity name (e.g. "jane.doe" → "Jane Doe"). */
function humanizeEntityName(name: string): string {
  return name
    .split(/[._-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Maps a catalog User entity to the lightweight TeamMember shape used by the
 * UI. Falls back gracefully when optional profile fields are missing.
 */
export function memberFromUserEntity(entity: Entity): TeamMember {
  const id = stringifyEntityRef(entity);
  const profile = (entity as UserEntity).spec?.profile;
  const name =
    profile?.displayName ??
    entity.metadata.title ??
    humanizeEntityName(entity.metadata.name);
  return {
    id,
    name,
    initials: initialsFromName(name),
    color: colorForKey(id),
  };
}

// ---------------------------------------------------------------------------
// Date helpers (local-time, no external dependencies)
// ---------------------------------------------------------------------------

/** Midnight today in local time. */
export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Formats a Date to a local ISO date string (YYYY-MM-DD). */
export function toIso(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parses a local ISO date string (YYYY-MM-DD) into a local Date at midnight. */
export function parseIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** True when `iso` falls within the inclusive [start, end] range of a leave. */
export function isLeaveOnDay(leave: LeaveEntry, iso: string): boolean {
  return iso >= leave.startDate && iso <= leave.endDate;
}

/** True when the leave is currently in progress (spans today). */
export function isLeaveActive(leave: LeaveEntry, todayIso: string): boolean {
  return isLeaveOnDay(leave, todayIso);
}

/** Inclusive number of calendar days a leave spans. */
export function leaveDurationDays(leave: LeaveEntry): number {
  const start = parseIso(leave.startDate);
  const end = parseIso(leave.endDate);
  const ms = end.getTime() - start.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24)) + 1;
}

/** Human friendly range, e.g. "Mar 3 – Mar 7" or "Mar 3" for a single day. */
export function formatLeaveRange(leave: LeaveEntry): string {
  const start = parseIso(leave.startDate);
  const end = parseIso(leave.endDate);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return leave.startDate === leave.endDate
    ? fmt(start)
    : `${fmt(start)} – ${fmt(end)}`;
}
